"use client"

import { useEffect, useState } from "react"
import { Loader2, RefreshCw } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { AI_USE_CASES } from "@/lib/ai/langchain/types"
import { AGENT_PRIMARY_ROLE_USE_CASE, type AgentRoleScopeType } from "@/lib/ai/roles/types"
import type { ComposedRoleBundle } from "@/lib/ai/roles/compose"
import { useComposeAgentRolesMutation } from "@/hooks/mutations/ai-use-case-roles"

interface ComposedPromptDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  scopeType: AgentRoleScopeType
  roleSlugs: string[]
  /** Receives the (possibly edited) bundle — the caller sends it on as `composed`. */
  onApply: (composed: ComposedRoleBundle) => Promise<void> | void
}

/**
 * Nothing is cached server-side, so the bundle shown here is the exact one that
 * gets applied — the caller hands it back instead of generating a second time.
 */
export function ComposedPromptDialog({
  open,
  onOpenChange,
  scopeType,
  roleSlugs,
  onApply,
}: ComposedPromptDialogProps) {
  const compose = useComposeAgentRolesMutation()
  const [bundle, setBundle] = useState<ComposedRoleBundle | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isApplying, setIsApplying] = useState(false)

  const primaryUseCase = AGENT_PRIMARY_ROLE_USE_CASE[scopeType]
  const classifierLabels = bundle?.[AI_USE_CASES.EMAIL_CLASSIFICATION]?.settings
    ?.classifier_labels as string[] | undefined

  const generate = async () => {
    setError(null)
    try {
      setBundle(await compose.mutateAsync({ scope_type: scopeType, role_slugs: roleSlugs }))
    } catch (err) {
      // Keep whatever is on screen: a failed REgeneration must not throw away the
      // previous composition (already paid for) together with the user's edits.
      setError(err instanceof Error ? err.message : "Failed to compose the prompt")
    }
  }

  // One generation per opening; Regenerate is explicit and costs another call.
  useEffect(() => {
    if (!open) {
      setBundle(null)
      setError(null)
      return
    }
    void generate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roleSlugs.join(",")])

  const isBusy = compose.isPending || isApplying

  return (
    <Dialog open={open} onOpenChange={(next) => !isBusy && onOpenChange(next)}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Review the merged prompt</DialogTitle>
          <DialogDescription>
            The selected roles were merged into one system prompt. Edit it if you want — what you
            see here is exactly what gets applied.
          </DialogDescription>
        </DialogHeader>

        {compose.isPending && (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Composing the prompt...</span>
          </div>
        )}

        {error && !compose.isPending && <p className="text-sm font-medium text-destructive">{error}</p>}

        {bundle && !compose.isPending && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="composed-prompt">System prompt</Label>
              <Textarea
                id="composed-prompt"
                value={bundle[primaryUseCase]?.system_prompt ?? ""}
                onChange={(event) =>
                  setBundle({
                    ...bundle,
                    [primaryUseCase]: {
                      ...bundle[primaryUseCase],
                      system_prompt: event.target.value,
                    },
                  })
                }
                rows={16}
                className="font-mono text-xs"
              />
            </div>

            {classifierLabels && (
              <div className="space-y-2">
                <Label>Email categories</Label>
                <div className="flex flex-wrap gap-1">
                  {classifierLabels.map((label) => (
                    <Badge key={label} variant="secondary" className="font-normal">
                      {label}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Categories come from the selected roles and cannot be edited here — changing them
                  would orphan the per-category settings.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button type="button" variant="ghost" onClick={() => void generate()} disabled={isBusy}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            <span>Regenerate</span>
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isBusy}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!bundle || isBusy}
              onClick={async () => {
                if (!bundle) return
                setIsApplying(true)
                setError(null)
                try {
                  await onApply(bundle)
                  onOpenChange(false)
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Failed to apply the prompt")
                } finally {
                  setIsApplying(false)
                }
              }}
            >
              {isApplying && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              <span>Apply</span>
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
